import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { HttpHeaders, HttpClient } from '@angular/common/http';
import { CredentialsService } from './credentials.service';
import { appConstants } from '@app/common/app-constants';
import { ProxyDataService } from '@app/core/http';
import { ExamSharingPageService } from '@app/di/exam-sharing/exam-sharing-page.service';
import { CookieService } from 'ngx-cookie-service';
import { Logger } from '@core/lib/services';

const log = new Logger('AuthenticationService');

export interface LoginContext {
  loginUsername: string;
  loginPassword: string;
  remember?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  codeVerifiers = '';
  state_random_string = '';
  queryParams: any;

  constructor(
    private dataService: ProxyDataService,
    private credentialsService: CredentialsService,
    private http: HttpClient,
    private examSharingPageService: ExamSharingPageService,
    private cookieService: CookieService // Inject CookieService
  ) {}

  login(context: LoginContext) {
    this.credentialsService.setCredentials(
      {
        username: context.loginUsername,
        token: '123456'
      },
      context.remember
    );

    return this.dataService.httpPost(
      appConstants.appURLs.login,
      {
        loginUserName: context.loginUsername,
        loginPassword: context.loginPassword
      },
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  //private redirectUri = 'https://localhost:4200/WFMHome'; // Must match auth server config
  private redirectUri = window.location.origin + appConstants.appURLs.wfmCallBackURL; // Must match auth server config
  // Initiates OAuth authorization code flow with and without PKCE for IDP login
  initiateAuthRequestSSOUser(pkceFlag: any, baseUrl: any, clientId: any, isUserActive: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.generatePKCECodes().then(
        data => {
          const authParams = this.createURLParms(clientId, data, pkceFlag, isUserActive);
          log.debug(
            'SSO : initiateAuthRequestSSOUser with pkce ',
            pkceFlag,
            ' URL ',
            `${baseUrl}?${authParams.toString()}`
          );
          resolve(`${baseUrl}?${authParams.toString()}`);
        },
        error => {
          reject(error);
        }
      );
    });
  }

  // Initiates OAuth authorization code flow with and without PKCE for service user login
  initiateAuthRequestServiceUser(pkceFlag: any, baseUrl: any, clientId: any, isUserActive: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.generatePKCECodes().then(
        data => {
          const authParams = this.createURLParms(clientId, data, pkceFlag, isUserActive);
          authParams.append('serviceUser', 'true');
          log.debug(
            'SSO : initiateAuthRequestServiceUser for service user with pkce ',
            pkceFlag,
            `${baseUrl}?${authParams.toString()}`
          );
          resolve(`${baseUrl}?${authParams.toString()}`);
        },
        error => {
          reject(error);
        }
      );
    });
  }

  private createURLParms(clientId: any, data: any, pkceFlag: any, isUserActive: any) {
    if (pkceFlag) {
      log.debug('SSO : data.codeVerifier', data.codeVerifier);
      this.cookieService.set('code_verifier', data.codeVerifier, {
        expires: appConstants.COOKIE_PROP.expires, // Cookie expires in 1 hour
        path: appConstants.COOKIE_PROP.path, // Path where the cookie is accessible
        secure: appConstants.COOKIE_PROP.secure, // Cookie is only sent over HTTPS
        sameSite: 'Strict' // SameSite policy
      });
      const parm = this.createSearchParamURL(clientId, isUserActive);
      parm.append('code_challenge', data.codeChallenge);
      parm.append('code_challenge_method', 'S256');
      return parm;
    } else {
      this.cookieService.delete('code_verifier', '/');
      log.debug('SSO : Code verifier cookie removed');
      return this.createSearchParamURL(clientId, isUserActive);
    }
  }

  private createSearchParamURL(clientId: any, isUserActive: any) {
    const parm = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: this.redirectUri,
      scope: 'openid',
      state: this.getQueryParams()
    });
    if (isUserActive) {
      return parm;
    } else {
      parm.append('prompt', 'login');
      return parm;
    }
  }

  private getQueryParams(): string {
    const sharedParams = this.examSharingPageService.getInitialLaunchParameters()?.encryptedExamIdentifier ?? '';

    // Ensure this.queryParams is an object
    if (!this.queryParams) {
      this.queryParams = {};
    }
    if (sharedParams) {
      this.queryParams['sharedParams'] = sharedParams;
    }

    const queryParamString =
      this.queryParams && Object.keys(this.queryParams).length ? JSON.stringify(this.queryParams) : '';
    log.debug('SSO : queryParamString', queryParamString);
    return this.generateRandomState() + '|' + queryParamString;
  }

  private generateRandomState(): string {
    const array = new Uint32Array(2);
    crypto.getRandomValues(array);
    return Array.from(array, num => num.toString(36))
      .join('')
      .substring(0, 11);
  }
  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */
  logout(): Observable<boolean> {
    this.credentialsService.setCredentials();
    return of(true);
  }

  // method to validate jwt token wfm for external app
  validateJwtToken(token) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token
      })
    };
    const data = {};
    return this.dataService.httpPost(appConstants.appURLs.verifyToken, data, httpOptions);
  }

  // Function to Base64 URL-encode a Uint8Array
  private base64urlEncode(buffer: Uint8Array): string {
    let result = btoa(String.fromCharCode(...buffer))
      .replace(/\+/g, '-') // Replace '+' with '-'
      .replace(/\//g, '_'); // Replace '/' with '_'
    // Remove '=' padding - safe approach without backtracking
    while (result.endsWith('=')) {
      result = result.slice(0, -1);
    }
    return result;
  }

  // Generate a valid code verifier (43–128 chars)
  private generateCodeVerifier(numBytes = 96): string {
    const randomValues = new Uint8Array(numBytes);
    crypto.getRandomValues(randomValues);
    const base64url = this.base64urlEncode(randomValues);
    // Ensure length is between 43 and 128 characters
    return base64url.slice(0, Math.min(base64url.length, 128));
  }

  // Generate PKCE code verifier and challenge per RFC 7636
  generatePKCECodes(): Promise<any> {
    const codeVerifier = this.generateCodeVerifier(96);
    // S256: SHA-256 hash the ASCII representation of the code verifier
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    return new Promise((resolve, reject) => {
      crypto.subtle.digest('SHA-256', data).then(
        hash => {
          const codeChallenge = this.base64urlEncode(new Uint8Array(hash));
          resolve({ codeVerifier: codeVerifier, codeChallenge: codeChallenge });
        },
        error => {
          reject(error);
        }
      );
    });
  }
}
